import { useEffect, useState } from "react";

import { tdApi } from "@src/services/api/tdApi";
import { useToastActions } from "@src/hooks/useToasts";

/**
 * Hook to fetch the quote currency for a symbol.
 * Returns null if inputs are empty, fetch fails, or symbol has no quote.
 * Shows a warning toast on fetch failure.
 */
export const useQuoteCurrency = (exchangeName: string, symbolName: string): string | null => {
  const [quoteCurrency, setQuoteCurrency] = useState<string | null>(null);
  const { showToast } = useToastActions();

  useEffect(() => {
    if (!exchangeName || !symbolName) {
      setQuoteCurrency(null);
      return;
    }

    let isCurrent = true;

    tdApi
      .getSymbol(exchangeName, symbolName)
      .then((symbol) => {
        if (isCurrent) {
          setQuoteCurrency(symbol?.quote ?? null);
        }
      })
      .catch((err) => {
        console.warn(
          "[useQuoteCurrency] Failed to fetch symbol %s/%s:",
          exchangeName,
          symbolName,
          err,
        );
        if (isCurrent) {
          setQuoteCurrency(null);
          showToast({
            type: "warning",
            title: "Quote Currency Unavailable",
            message: `Could not fetch quote currency for ${symbolName}`,
            timeout: 5000,
          });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [exchangeName, symbolName, showToast]);

  return quoteCurrency;
};
